/**
 * Skeleton matching the shape of the real DataTable used by table view.
 *
 * 4 column placeholders + a trailing action-cell stub so the column widths
 * land near the real ones when data arrives — avoids a horizontal layout
 * shift when the rows hydrate. If the columns in projectsPageColumns.tsx
 * change, update the slot widths here in the same PR.
 */
interface ProjectsTableSkeletonProps {
  /** Row count. Default 6 matches the real grid skeleton's slot count. */
  rows?: number;
}

const SLOT_WIDTHS = ['w-32', 'w-10', 'w-12', 'w-10', 'w-20'];

export function ProjectsTableSkeleton({ rows = 6 }: ProjectsTableSkeletonProps) {
  return (
    <div
      role="status"
      aria-busy="true"
      aria-live="polite"
      className="bg-white rounded-lg border border-[var(--color-card-stroke)] overflow-hidden skeleton-fade-in"
    >
      <table className="w-full">
        <thead className="bg-gray-50/50 border-b border-gray-100">
          <tr>
            <th className="w-8 pl-3 pr-0 py-3" aria-hidden="true">
              <div className="w-4 h-4 rounded skeleton-shimmer" />
            </th>
            {SLOT_WIDTHS.map((w, i) => (
              <th key={i} className="px-4 py-3 text-left" aria-hidden="true">
                <div className={`h-3 ${w} rounded skeleton-shimmer`} />
              </th>
            ))}
            <th className="w-12 px-3 py-3" aria-hidden="true">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }).map((_, rowIndex) => (
            <tr key={rowIndex} className={rowIndex % 2 === 1 ? 'bg-gray-50/60' : ''}>
              <td className="w-8 pl-3 pr-0 py-3">
                <div className="w-4 h-4 rounded skeleton-shimmer" />
              </td>
              {SLOT_WIDTHS.map((w, i) => (
                <td key={i} className="px-4 py-3">
                  <div
                    className={`h-3.5 ${w} rounded skeleton-shimmer`}
                    style={{ animationDelay: `${rowIndex * 80 + i * 40}ms` }}
                  />
                </td>
              ))}
              <td className="w-12 px-3 py-3">
                <div className="w-4 h-4 rounded skeleton-shimmer" />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <span className="sr-only">Loading projects</span>
    </div>
  );
}
